import React, { useState, useEffect } from 'react';
import { X, Loader } from 'lucide-react';
import { changeHistoryService, ChangeHistory } from '../services/changeHistoryService';
import ChangeHistoryTimeline from './ChangeHistoryTimeline';
import { toast } from 'react-toastify';

interface ChangeHistoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  entityType: string;
  entityId: string;
}

const ChangeHistoryDrawer: React.FC<ChangeHistoryDrawerProps> = ({
  isOpen,
  onClose,
  workspaceId,
  entityType,
  entityId
}) => {
  const [history, setHistory] = useState<ChangeHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && workspaceId && entityType && entityId) {
      fetchHistory();
    }
  }, [isOpen, workspaceId, entityType, entityId]);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await changeHistoryService.getHistory(workspaceId, entityType, entityId);
      setHistory(data);
    } catch (err: any) {
      console.error('Error fetching change history:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load change history';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 2100,
        }}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '55%',
          maxWidth: '600px',
          backgroundColor: 'white',
          boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.15)',
          zIndex: 2101,
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideInRight 0.3s ease-out',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #E5E7EB',
            background: 'linear-gradient(135deg, #48182F 0%, #5a1f3d 100%)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: 'white',
            }}
          >
            Change History
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              color: 'white',
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
            aria-label="Close drawer"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px',
          }}
        >
          {loading ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 20px',
              }}
            >
              <Loader
                size={40}
                style={{
                  color: '#48182F',
                  animation: 'spin 1s linear infinite',
                  marginBottom: '16px',
                }}
              />
              <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
                Loading change history...
              </p>
            </div>
          ) : error ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 20px',
              }}
            >
              <p style={{ color: '#DC2626', fontSize: '16px', margin: '0 0 12px 0' }}>
                {error}
              </p>
              <button
                onClick={fetchHistory}
                style={{
                  backgroundColor: '#48182F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                Try Again
              </button>
            </div>
          ) : history.length === 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 20px',
              }}
            >
              <p style={{ color: '#6B7280', fontSize: '16px', margin: 0 }}>
                No changes recorded yet
              </p>
            </div>
          ) : (
            <ChangeHistoryTimeline history={history} />
          )}
        </div>
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </>
  );
};

export default ChangeHistoryDrawer;
